import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useTheme } from '../contexts/ThemeContext';
import { useUnsavedGuard } from '../contexts/UnsavedGuardContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check, FileText, Download, Loader2, Save, AlertCircle, Pencil, CloudOff, CheckCircle2 } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { track } from '../utils/observability';
import { SRCPanel } from '../components/SRCPanel';
import { AttachmentsPanel } from '../components/AttachmentsPanel';
import { AuditTrailSection } from '../components/AuditTrailSection';
import { useAuth } from '../contexts/AuthContext';
import { useDraftAutosave } from '../hooks/useDraftAutosave';

// Funnel events: normalize consultation_type per analytics spec.
const normalizeFunnelType = (t) => (t === 'avaliacao' ? 'anamnese' : t === 'evolucao' ? 'evolution' : t);

// Defensive helper: Gemini sometimes returns scalars/objects where the UI
// expects an array of strings (Sentry "g.map is not a function"). This
// normalises any input into a renderable list of strings.
const toArr = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') return [value];
  if (typeof value === 'object') return Object.values(value).filter(Boolean);
  return [String(value)];
};

// Dual-key lookup: English first, then Portuguese fallback. Safety net for
// old documents or future Gemini drift. Returns undefined if nothing found.
const EN_PT_ALIASES = {
  // anamnesis
  main_complaint: 'queixa_principal',
  current_history: 'historia_doenca_atual',
  medical_history: 'historico_medico',
  lifestyle: 'estilo_de_vida',
  patient_expectations: 'expectativas_do_paciente',
  // physical_exam
  inspection: 'inspecao',
  palpation: 'palpacao',
  range_of_motion: 'amplitude_de_movimento',
  special_tests: 'testes_especiais',
  functional_assessment: 'avaliacao_funcional',
  // functional_diagnosis
  cif_diagnosis: 'diagnostico_cinetico_funcional',
  identified_problems: 'problemas_identificados',
  contextual_factors: 'fatores_contextuais',
  prognosis: 'prognostico',
  // treatment_plan phase
  phase_number: 'fase',
  techniques: 'intervencoes',
  // patient_guidelines
  home_exercises: 'exercicios_domiciliares',
  precautions: 'precaucoes',
  recommendations: 'recomendacoes',
  return_instructions: 'instrucoes_retorno',
};
const dget = (obj, enKey) => {
  if (!obj || typeof obj !== 'object') return undefined;
  const v = obj[enKey];
  if (v !== undefined && v !== null && v !== '') return v;
  // 'expectations' legacy alias
  if (enKey === 'patient_expectations' && obj.expectations) return obj.expectations;
  const ptAlt = EN_PT_ALIASES[enKey];
  if (ptAlt && obj[ptAlt] !== undefined && obj[ptAlt] !== null && obj[ptAlt] !== '') {
    return obj[ptAlt];
  }
  return v;
};

// ─── Inline editing helpers ───────────────────────────────────────────────
// Deep clone + set a value at a dotted path inside a plain object. Used to
// build the partial `clinical_documentation` payload sent to PUT.
// Path supports numeric segments to index arrays:
//   "treatment_plan.0.objectives"  → sets out.treatment_plan[0].objectives
const deepSetPath = (obj, path, value) => {
  const out = obj ? JSON.parse(JSON.stringify(obj)) : {};
  const keys = path.split('.');
  let cursor = out;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const nextK = keys[i + 1];
    const nextIsIndex = /^\d+$/.test(nextK);
    if (cursor[k] == null || (nextIsIndex && !Array.isArray(cursor[k]))
        || (!nextIsIndex && typeof cursor[k] !== 'object')) {
      cursor[k] = nextIsIndex ? [] : {};
    }
    cursor = cursor[k];
  }
  const last = keys[keys.length - 1];
  // For array index target, ensure array exists at the slot
  if (/^\d+$/.test(last) && !Array.isArray(cursor)) {
    // edge case; just assign
  }
  cursor[last] = value;
  return out;
};

// EditableText: in-place edit affordance for clinical_documentation fields.
// Renders read-only paragraph by default; on click (or programmatic activate)
// flips into a textarea with Save/Cancel. Multiline arrays are joined with
// newlines for editing and split back on save.
const EditableText = ({
  value,
  fieldPath,            // 'anamnesis.main_complaint' or 'treatment_plan.0.objectives'
  multiline = false,    // true when source value is an array of strings
  editable,             // only true when consultation status is in_review
  isDark,
  emptyPlaceholder = 'Clique para adicionar informação',
  activeField,
  setActiveField,
  onSave,               // async (path, newValue) => void
}) => {
  const isActive = activeField === fieldPath;
  const initial = multiline ? (Array.isArray(value) ? value.join('\n') : (value || '')) : (value || '');
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const textareaRef = useRef(null);

  // Reset draft whenever value changes from outside (e.g. after refresh).
  useEffect(() => {
    setDraft(initial);
  }, [value, initial]);

  // Auto-focus when the user (or modal) opens edit mode.
  useEffect(() => {
    if (isActive && textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [isActive]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const out = multiline
        ? draft.split('\n').map((s) => s.trim()).filter(Boolean)
        : draft.trim();
      await onSave(fieldPath, out);
      setSavedFlash(true);
      setActiveField(null);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!editable) {
    // Read-only — original render.
    if (multiline) {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      if (!arr.length) return <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>{emptyPlaceholder}</p>;
      return (
        <ul className={`list-disc list-inside space-y-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
          {arr.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    }
    return <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>{value || emptyPlaceholder}</p>;
  }

  if (isActive) {
    return (
      <div className="space-y-2" data-testid={`editable-${fieldPath.replace(/\./g, '-')}-active`}>
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={multiline ? 4 : 2}
          className={`w-full rounded-lg border p-3 text-sm font-medium ${
            isDark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-300 text-slate-900'
          } focus:outline-none focus:ring-2 focus:ring-primary`}
          placeholder={emptyPlaceholder + (multiline ? ' (um item por linha)' : '')}
          data-testid={`edit-textarea-${fieldPath.replace(/\./g, '-')}`}
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`save-field-${fieldPath.replace(/\./g, '-')}`}
          >
            {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
            Salvar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setDraft(initial); setActiveField(null); }}
            disabled={saving}
            data-testid={`cancel-field-${fieldPath.replace(/\./g, '-')}`}
          >
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  // Read-with-edit affordance (clickable to enter edit mode).
  const hasValue = multiline
    ? (Array.isArray(value) ? value.length > 0 : Boolean(value))
    : Boolean(value);

  const handleClick = () => setActiveField(fieldPath);

  return (
    <div
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleClick()}
      className={`group cursor-pointer rounded-md transition-colors -mx-2 px-2 py-1 ${
        isDark ? 'hover:bg-slate-700/40' : 'hover:bg-slate-100'
      }`}
      data-testid={`editable-${fieldPath.replace(/\./g, '-')}-readonly`}
    >
      {multiline ? (
        hasValue ? (
          <ul className={`list-disc list-inside space-y-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
            {(Array.isArray(value) ? value : [value]).map((it, i) => <li key={i}>{it}</li>)}
          </ul>
        ) : (
          <p className={isDark ? 'text-slate-500 italic' : 'text-slate-400 italic'}>{emptyPlaceholder}</p>
        )
      ) : (
        <p className={hasValue ? (isDark ? 'text-slate-300' : 'text-slate-700') : (isDark ? 'text-slate-500 italic' : 'text-slate-400 italic')}>
          {value || emptyPlaceholder}
        </p>
      )}
      <span className={`text-xs inline-flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${
        isDark ? 'text-primary' : 'text-primary'
      }`}>
        <Pencil className="h-3 w-3" /> Clique para editar
      </span>
      {savedFlash && (
        <span className="text-xs text-emerald-500 ml-2 font-medium">✓ Salvo</span>
      )}
    </div>
  );
};

export default function ConsultationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [consultation, setConsultation] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [srcReady, setSrcReady] = useState(false);
  const [p4Dialog, setP4Dialog] = useState({ open: false, mode: null });
  const [p4Working, setP4Working] = useState(false);
  // P2 AUTOSAVE — working document (in-memory). Never persisted to
  // clinical_documentation until approval; the hook streams it to the server
  // draft slot with optimistic concurrency.
  const [workingDoc, setWorkingDoc] = useState(null);
  // COFFITO 414 — missing required fields surfaced after server-side 422 on approval.
  const [missingFields, setMissingFields] = useState(null); // null | array of {field, label, section}
  const [missingMessage, setMissingMessage] = useState('');
  const [showMissingModal, setShowMissingModal] = useState(false);

  // Inline edit — `activeField` holds the dotted path of the field currently
  // in edit mode (only one at a time).
  const [activeField, setActiveField] = useState(null);

  // ── P2 AUTOSAVE hook ────────────────────────────────────────────────────
  // Enabled only while the consultation is editable (draft / in_review).
  const autosaveEnabled = consultation?.status === 'draft' || consultation?.status === 'in_review';
  const autosave = useDraftAutosave({
    consultationId: id,
    enabled: autosaveEnabled,
    onDeleted: () => {
      toast.error('Consulta excluída.');
      navigate('/consultations');
    },
    // When the user picks "usar versão servidor" from the conflict dialog,
    // hydrate the working doc with the server snapshot.
    onLoadServer: (server) => {
      if (server?.draftContent) setWorkingDoc(server.draftContent);
    },
  });

  // Save a single field. Updates the in-memory working document and marks
  // it dirty on the autosave hook. Does NOT touch clinical_documentation.
  const saveField = async (path, value) => {
    const base = workingDoc || consultation?.clinical_documentation || {};
    const updated = deepSetPath(base, path, value);
    setWorkingDoc(updated);
    autosave.markDirty(updated);
    return { clinical_documentation: updated };
  };

  // Append a new empty phase to treatment_plan. Used when the AI returned
  // no phases OR when the user wants to add another phase manually.
  const [addingPhase, setAddingPhase] = useState(false);
  const handleAddPhase = async () => {
    if (addingPhase) return;
    setAddingPhase(true);
    try {
      const base = workingDoc || consultation?.clinical_documentation || {};
      const currentPhases = Array.isArray(base.treatment_plan) ? base.treatment_plan : [];
      const newPhase = {
        phase_number: currentPhases.length + 1,
        duration: '',
        objectives: [],
        techniques: [],
        frequency: '',
        progression_criteria: [],
      };
      const updated = { ...base, treatment_plan: [...currentPhases, newPhase] };
      setWorkingDoc(updated);
      autosave.markDirty(updated);
      // Auto-activate edit on the new phase's objectives so the user can start typing immediately.
      const newIdx = currentPhases.length;
      setTimeout(() => setActiveField(`treatment_plan.${newIdx}.objectives`), 200);
    } finally {
      setAddingPhase(false);
    }
  };
  const { isDark } = useTheme();
  const { setGuard, clearGuard } = useUnsavedGuard();
  const { user } = useAuth();
  // Guard so doc_reviewed fires only once per consultation view.
  const reviewedFiredRef = React.useRef(false);

  useEffect(() => {
    loadConsultation();
  }, [id]);

  // AUTH-01 unsaved guard: while consultation is in DRAFT or IN_REVIEW,
  // register a logout-time prompt so the autosave flushes before leaving.
  useEffect(() => {
    const st = consultation?.status;
    if (st === 'draft' || st === 'in_review') {
      setGuard({
        onSaveDraft: async () => {
          try {
            const ok = await autosave.flush();
            if (ok === false) throw new Error('flush-failed');
            toast.success('Rascunho salvo antes de sair.');
          } catch (err) {
            toast.error('Não foi possível salvar o rascunho.');
            throw err;
          }
        },
      });
    } else {
      clearGuard();
    }
    return () => clearGuard();
  }, [consultation?.status, id, setGuard, clearGuard, autosave]);

  const loadConsultation = async () => {
    try {
      const data = await api.getConsultation(id);
      setConsultation(data);
      // Hydrate the P2 working document: prefer server-side draftContent (a
      // previous session's autosave) when present, otherwise fall back to the
      // authoritative clinical_documentation. Sync the hook's baseRevision.
      const initialDoc = data?.draftContent || data?.clinical_documentation || null;
      setWorkingDoc(initialDoc);
      autosave.setRevision(Number(data?.draftRevision) || 0);
      // Funnel event #4 — doc_reviewed (fires once per view, only when the
      // consultation actually has a document to review).
      if (!reviewedFiredRef.current && data?.clinical_documentation) {
        reviewedFiredRef.current = true;
        track('doc_reviewed', {
          user_id: user?.id || null,
          consultation_type: normalizeFunnelType(data?.consultation_type),
          timestamp: new Date().toISOString(),
        });
      }
      // SRC gate: only required for "avaliacao" — evolucao/already-approved bypass it
      const isAvaliacao = data.consultation_type === 'avaliacao';
      const alreadyConfirmed = Boolean(data.src_confirmed);
      const isApproved = data.status === 'approved' || data.status === 'exported';
      setSrcReady(!isAvaliacao || alreadyConfirmed || isApproved);

      if (data.patient_id) {
        const patientData = await api.getPatient(data.patient_id);
        setPatient(patientData);
      }
    } catch (error) {
      toast.error('Erro ao carregar consulta');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (approving) return;
    setApproving(true);
    try {
      // Block approval while a conflict is unresolved — the user must choose
      // "keepMine" or "loadServer" first, otherwise we might commit stale data.
      if (autosave.conflict) {
        toast.error('Resolva o conflito de edição antes de aprovar.');
        setApproving(false);
        return;
      }
      // Ensure the latest in-memory edits are on the server draft before we
      // commit them into the clinical record. If the flush produces a 409
      // conflict, the hook will surface it; we abort approval.
      const flushed = await autosave.flush();
      if (flushed === false && autosave.conflict) {
        toast.error('Conflito detectado. Reveja antes de aprovar.');
        setApproving(false);
        return;
      }
      const docToCommit = workingDoc || consultation?.clinical_documentation || null;
      const result = await api.updateConsultation(id, {
        clinical_documentation: docToCommit,
        status: 'approved',
      });
      // Funnel event #5 — doc_approved
      track('doc_approved', {
        user_id: user?.id || null,
        consultation_type: normalizeFunnelType(consultation?.consultation_type),
        timestamp: new Date().toISOString(),
      });
      // NPS-01 — backend may include `show_nps: true` when this is the 5th
      // approval. Stash a flag so the Dashboard surfaces the survey on the
      // next mount. localStorage is fine here: it lives on the same origin
      // and the Dashboard clears the flag on read.
      if (result?.show_nps) {
        try { localStorage.setItem('pending_nps', 'true'); } catch { /* ignore */ }
      }
      // Approval succeeded → clear any leftover missing-fields highlight.
      setMissingFields(null);
      setMissingMessage('');
      setShowMissingModal(false);
      toast.success('Documentação aprovada!');
      loadConsultation();
    } catch (error) {
      // COFFITO 414/2012 — server returned 422 with INCOMPLETE_DOCUMENTATION.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 422 && detail?.error === 'INCOMPLETE_DOCUMENTATION') {
        setMissingFields(detail.missing_fields || []);
        setMissingMessage(detail.message || 'Documento incompleto.');
        setShowMissingModal(true);
        // Do NOT show a generic toast — modal carries the information.
        return;
      }
      toast.error('Erro ao aprovar documentação');
    } finally {
      setApproving(false);
    }
  };

  // Map of missing field paths → true (used to highlight rendered sections).
  const missingFieldSet = React.useMemo(() => {
    const set = new Set();
    (missingFields || []).forEach((m) => set.add(m.field));
    return set;
  }, [missingFields]);

  // Class helpers for highlighting fields flagged as missing.
  const highlightClass = (fieldPath) =>
    missingFieldSet.has(fieldPath)
      ? 'border-l-4 border-orange-500 pl-3 bg-orange-50 dark:bg-orange-900/10 rounded-r'
      : '';

  // Scroll to the first missing field after the modal is dismissed.
  // Maps validation path (e.g. "anamnesis.main_complaint") to the editable
  // field path used by EditableText. For collection-level errors
  // ("treatment_plan", "treatment_plan.phase.objectives"), activate the
  // first phase's relevant field.
  const scrollToFirstMissingField = () => {
    if (!missingFields || missingFields.length === 0) return;
    const first = missingFields[0].field;
    // Map validation path → concrete editable path
    let editablePath = first;
    if (first === 'treatment_plan') editablePath = 'treatment_plan.0.objectives';
    if (first === 'treatment_plan.phase.objectives') editablePath = 'treatment_plan.0.objectives';
    if (first === 'treatment_plan.phase.techniques') editablePath = 'treatment_plan.0.techniques';

    setTimeout(() => {
      const el = document.querySelector(`[data-field="${first}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Activate inline edit on the resolved field path.
      setActiveField(editablePath);
    }, 120);
  };

  const handleExportPdf = async () => {
    if (!consultation?.clinical_documentation) {
      toast.error('Esta consulta ainda não possui documentação clínica');
      return;
    }
    
    setExporting(true);
    try {
      const blob = await api.exportPdf(id);
      track('consultation_exported_pdf', { consultation_type: consultation?.consultation_type, template_type: consultation?.template_type });
      // Funnel event #6 — doc_exported (normalized spec)
      track('doc_exported', {
        user_id: user?.id || null,
        consultation_type: normalizeFunnelType(consultation?.consultation_type),
        timestamp: new Date().toISOString(),
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Format filename: Prontuario_[PatientID]_[YYYY-MM-DD].pdf
      const patientId = patient?.name?.replace(/\s+/g, '_') || patient?.initials || 'Paciente';
      const date = new Date().toISOString().split('T')[0];
      link.download = `Prontuario_${patientId}_${date}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF gerado com sucesso!');
      loadConsultation(); // Reload to update status
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.code === 'FEATURE_NOT_AVAILABLE') {
        toast.error('Exportação de PDF disponível apenas nos planos Solo e Clínica.');
      } else {
        toast.error('Erro ao gerar PDF. Tente novamente.');
      }
    } finally {
      setExporting(false);
    }
  };

  const handleSaveDraft = async () => {
    // Legacy DRAFT-01 explicit "Salvar Rascunho" — kept only for the
    // UnsavedGuard fallback (route change while dirty). The button was
    // removed in P2; edits now flow through the autosave hook.
    try {
      await autosave.flush();
    } catch { /* the hook already surfaces status */ }
  };

  // P4 — archive / restore / delete actions. All share the same modal so
  // the user sees the disposition (credit refund / no refund) explicitly.
  const handleP4Confirm = async () => {
    if (p4Working) return;
    setP4Working(true);
    try {
      if (p4Dialog.mode === 'archive') {
        await api.archiveConsultation(id);
        toast.success('Documento arquivado.');
        setP4Dialog({ open: false, mode: null });
        loadConsultation();
      } else if (p4Dialog.mode === 'restore') {
        await api.restoreConsultation(id);
        toast.success('Documento restaurado.');
        setP4Dialog({ open: false, mode: null });
        loadConsultation();
      } else if (p4Dialog.mode === 'delete') {
        const result = await api.deleteConsultation(id);
        setP4Dialog({ open: false, mode: null });
        const refund = result?.creditRefund;
        if (refund?.refunded) {
          toast.success('Documento excluído. Crédito estornado automaticamente.');
        } else if (refund?.eligible && refund?.reasonCode === 'WINDOW_EXPIRED') {
          toast.info('Documento excluído. Fora da janela de estorno automático — enviado para revisão manual.');
        } else if (refund?.eligible && refund?.reasonCode === 'CAP_EXCEEDED') {
          toast.info('Documento excluído. Limite mensal de estornos atingido — enviado para revisão manual.');
        } else {
          toast.success('Documento excluído.');
        }
        // Approved delete or draft delete → both leave the page.
        navigate('/consultations');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível concluir a operação.');
    } finally {
      setP4Working(false);
    }
  };

  const getStatusBadge = (status) => {
    const isAvaliacao = consultation?.consultation_type === 'avaliacao';
    const srcConfirmed = Boolean(consultation?.src_confirmed);
    if (status === 'draft') {
      return {
        label: '● Rascunho',
        sub: 'Documento ainda não gerado · não é prontuário válido',
        className: isDark ? 'bg-amber-900/40 text-amber-300 border border-amber-800' : 'bg-amber-100 text-amber-700 border border-amber-200',
      };
    }
    if (status === 'in_review') {
      if (isAvaliacao && !srcConfirmed) {
        return {
          label: '● Em Revisão',
          sub: 'Aguardando confirmação da Síntese · não é prontuário válido',
          className: isDark ? 'bg-amber-900/40 text-amber-300 border border-amber-800' : 'bg-amber-100 text-amber-700 border border-amber-200',
        };
      }
      return {
        label: '● Pronto para Salvar',
        sub: isAvaliacao
          ? 'Síntese confirmada · clique em Aprovar para finalizar'
          : 'Pronto para aprovar como prontuário oficial',
        className: isDark ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-800' : 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      };
    }
    if (status === 'approved' || status === 'exported') {
      return {
        label: '✓ Prontuário Confirmado',
        sub: 'Registro clínico válido · COFFITO 414/2012',
        className: isDark ? 'bg-emerald-700 text-white border border-emerald-600' : 'bg-emerald-600 text-white border border-emerald-700',
      };
    }
    return { label: status, sub: '', className: '' };
  };

  // Relative-time helper (e.g. "há 2 horas")
  const relativeTime = (iso) => {
    if (!iso) return null;
    try {
      const then = new Date(iso).getTime();
      const diff = Date.now() - then;
      const m = Math.floor(diff / 60000);
      if (m < 1) return 'há instantes';
      if (m < 60) return `há ${m} minuto${m > 1 ? 's' : ''}`;
      const h = Math.floor(m / 60);
      if (h < 24) return `há ${h} hora${h > 1 ? 's' : ''}`;
      const d = Math.floor(h / 24);
      return `há ${d} dia${d > 1 ? 's' : ''}`;
    } catch { return null; }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!consultation) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className={`mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Consulta não encontrada</p>
          <Button onClick={() => navigate('/consultations')} variant="outline">
            Voltar para consultas
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const doc = workingDoc || consultation.clinical_documentation;
  const badge = getStatusBadge(consultation.status);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="consultation-detail-page">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className={`mb-4 ${isDark ? 'text-slate-400 hover:text-white' : ''}`}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div className="flex justify-between items-start gap-4">
            <div>
              <h1 className={`text-3xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>
                Documentação Clínica
              </h1>
              <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
                Paciente: {patient?.name || 'Carregando...'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 max-w-xs text-right">
              <span
                className={`px-4 py-2 rounded-full text-sm font-medium ${badge.className}`}
                data-testid={`consultation-status-badge-${consultation.status}`}
              >
                {badge.label}
              </span>
              {badge.sub && (
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`} data-testid="consultation-status-sub">
                  {badge.sub}
                </p>
              )}
              {(consultation.status === 'draft' || consultation.status === 'in_review') && (
                <p
                  className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                  data-testid="autosave-indicator"
                >
                  {autosave.status === 'saving' && 'Salvando…'}
                  {autosave.status === 'saved' && autosave.lastSavedAt &&
                    `Salvo automaticamente às ${new Date(autosave.lastSavedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
                  {autosave.status === 'dirty' && 'Alterações não salvas…'}
                  {autosave.status === 'failed' && (
                    <span className="text-amber-500">Falha ao salvar — nova tentativa em instantes</span>
                  )}
                  {autosave.status === 'conflict' && (
                    <span className="text-orange-500">Conflito de edição detectado</span>
                  )}
                  {autosave.status === 'pristine' && !autosave.lastSavedAt && consultation.draft_saved_at &&
                    `Salvo ${relativeTime(consultation.draft_saved_at)}`}
                </p>
              )}
            </div>
          </div>

          {/* Non-removable LGPD/COFFITO disclaimer for unconfirmed records */}
          {(consultation.status === 'draft' || consultation.status === 'in_review') && (
            <div
              className={`mt-4 flex items-start gap-3 p-3 rounded-lg border ${
                isDark ? 'bg-amber-950/30 border-amber-900/50 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-900'
              }`}
              data-testid="draft-disclaimer"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <p className="text-xs">
                Este documento ainda não foi confirmado. <strong>Não constitui prontuário clínico válido</strong> nos
                termos da Res. COFFITO 414/2012. Confirme para que tenha validade como registro clínico oficial.
              </p>
            </div>
          )}

          {/* P2 AUTOSAVE — offline banner (only visible when editable + offline). */}
          {autosaveEnabled && autosave.isOffline && (
            <div
              className={`mt-3 flex items-start gap-3 p-3 rounded-lg border ${
                isDark ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-slate-100 border-slate-300 text-slate-800'
              }`}
              data-testid="autosave-offline-banner"
            >
              <CloudOff className="w-4 h-4 shrink-0 mt-0.5" />
              <p className="text-xs">
                Sem conexão — alterações não salvas. Mantenha esta aba aberta; sincronizaremos automaticamente quando a rede voltar.
              </p>
            </div>
          )}

          {/* P2 AUTOSAVE — previous-session unsynced warning (A+C pointer). */}
          {autosaveEnabled && autosave.unsyncedFromPreviousSession && (
            <div
              className={`mt-3 flex items-start justify-between gap-3 p-3 rounded-lg border ${
                isDark ? 'bg-yellow-950/30 border-yellow-900/50 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-900'
              }`}
              data-testid="autosave-unsynced-warning"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-xs">
                  Havia alterações não sincronizadas na sessão anterior. Verifique o documento — se algum campo estiver faltando, edite novamente para restaurar.
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={autosave.acknowledgeUnsyncedWarning}
                data-testid="autosave-unsynced-dismiss"
              >
                Entendi
              </Button>
            </div>
          )}

          {/* COFFITO 414 — non-blocking review reminder while document is in_review */}
          {consultation.status === 'in_review' && (
            <div
              className={`mt-3 flex items-center justify-between gap-3 p-3 rounded-lg border ${
                isDark ? 'bg-orange-950/30 border-orange-900/50 text-orange-200' : 'bg-orange-50 border-orange-200 text-orange-900'
              }`}
              data-testid="review-reminder-banner"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-xs">
                  <strong>⚠️ Revise o documento antes de aprovar.</strong> Campos obrigatórios em branco
                  impedirão a aprovação. <em>Clique em qualquer campo do documento para editá-lo.</em>
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const el = document.querySelector('[data-field="anamnesis.main_complaint"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  setTimeout(() => setActiveField('anamnesis.main_complaint'), 120);
                }}
                className={isDark ? 'border-orange-700 text-orange-200 hover:bg-orange-900/40' : 'border-orange-300 text-orange-800 hover:bg-orange-100'}
                data-testid="edit-fields-btn"
              >
                <Pencil className="h-3 w-3 mr-1" />
                ✏️ Editar campos
              </Button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 mb-8">
          {consultation.status === 'in_review' && (
            <Button
              onClick={handleApprove}
              className="bg-primary hover:bg-primary-hover"
              disabled={!srcReady || approving || Boolean(autosave.conflict)}
              title={
                autosave.conflict
                  ? 'Resolva o conflito de edição antes de aprovar.'
                  : (!srcReady ? 'Confirme a Síntese de Raciocínio Clínico abaixo antes de aprovar.' : '')
              }
              data-testid="approve-btn"
            >
              {approving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Aprovar Documentação
            </Button>
          )}

          {/* PDF export is fully HIDDEN on DRAFT/IN_REVIEW — never disabled */}
          {(consultation.status === 'approved' || consultation.status === 'exported') && (
            <Button
              onClick={handleExportPdf}
              variant="outline"
              className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}
              disabled={exporting || !doc}
              data-testid="export-pdf-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gerando PDF...
                </>
              ) : (
                <>
                <Download className="h-4 w-4 mr-2" />
                Exportar PDF
              </>
            )}
          </Button>
          )}

          {/* P4 — Archive & Delete on approved/exported. Archive keeps the
              credit consumed; Delete may auto-refund within window+cap. */}
          {(consultation.status === 'approved' || consultation.status === 'exported') && (
            <>
              <Button
                onClick={() => setP4Dialog({ open: true, mode: 'archive' })}
                variant="outline"
                className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}
                data-testid="archive-btn"
              >
                Arquivar
              </Button>
              <Button
                onClick={() => setP4Dialog({ open: true, mode: 'delete' })}
                variant="outline"
                className="text-red-500 hover:text-red-700 border-red-200 hover:border-red-300 dark:border-red-800 dark:hover:border-red-700"
                data-testid="delete-approved-btn"
              >
                Excluir
              </Button>
            </>
          )}

          {/* P4 — Restore from archived */}
          {consultation.status === 'archived' && (
            <Button
              onClick={() => setP4Dialog({ open: true, mode: 'restore' })}
              variant="outline"
              className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}
              data-testid="restore-btn"
            >
              Restaurar
            </Button>
          )}
        </div>

        {/* Documentation Sections */}
        {doc ? (
          (() => {
            // Single source of truth for whether inline editing is active.
            const editable = consultation.status === 'in_review';
            // Resolve canonical (EN) anamnesis key to write to. Because the
            // document may have legacy PT-BR keys, we still SAVE to the EN key
            // and downstream reads use `dget()` to fall back. Reading
            // with `dget` ensures the textarea is pre-populated regardless.
            const editProps = { editable, isDark, activeField, setActiveField, onSave: saveField };
            return (
          <div className="space-y-6">
            {/* Anamnese */}
            <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-xl border p-6`}>
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-4 flex items-center gap-2`}>
                <FileText className="h-5 w-5 text-primary" />
                Anamnese
              </h2>
              <div className="space-y-4">
                <div data-field="anamnesis.main_complaint" className={highlightClass('anamnesis.main_complaint')}>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2 flex items-center gap-2`}>
                    {missingFieldSet.has('anamnesis.main_complaint') && (
                      <AlertCircle className="h-4 w-4 text-orange-500" data-testid="missing-field-icon" />
                    )}
                    Queixa Principal
                  </h3>
                  <EditableText value={dget(doc.anamnesis, 'main_complaint')} fieldPath="anamnesis.main_complaint" {...editProps} emptyPlaceholder="Descreva a queixa principal do paciente" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>História da Doença Atual</h3>
                  <EditableText value={dget(doc.anamnesis, 'current_history')} fieldPath="anamnesis.current_history" {...editProps} emptyPlaceholder="Descreva a história da doença atual" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Histórico Médico</h3>
                  <EditableText value={dget(doc.anamnesis, 'medical_history')} fieldPath="anamnesis.medical_history" {...editProps} emptyPlaceholder="Histórico médico relevante" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Estilo de Vida</h3>
                  <EditableText value={dget(doc.anamnesis, 'lifestyle')} fieldPath="anamnesis.lifestyle" {...editProps} emptyPlaceholder="Hábitos de vida, atividade física, trabalho" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Expectativas do Paciente</h3>
                  <EditableText value={dget(doc.anamnesis, 'patient_expectations')} fieldPath="anamnesis.patient_expectations" {...editProps} emptyPlaceholder="O que o paciente espera do tratamento" />
                </div>
              </div>
            </div>

            {/* Exame Físico */}
            <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-xl border p-6`}>
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-4`}>Exame Físico</h2>
              <div className="space-y-4">
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Inspeção</h3>
                  <EditableText value={dget(doc.physical_exam, 'inspection')} fieldPath="physical_exam.inspection" {...editProps} emptyPlaceholder="Achados da inspeção" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Palpação</h3>
                  <EditableText value={dget(doc.physical_exam, 'palpation')} fieldPath="physical_exam.palpation" {...editProps} emptyPlaceholder="Achados da palpação" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Amplitude de Movimento</h3>
                  <EditableText value={dget(doc.physical_exam, 'range_of_motion')} fieldPath="physical_exam.range_of_motion" {...editProps} emptyPlaceholder="Amplitude de movimento" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Testes Especiais</h3>
                  <EditableText value={dget(doc.physical_exam, 'special_tests')} fieldPath="physical_exam.special_tests" {...editProps} emptyPlaceholder="Testes especiais realizados e resultados" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Avaliação Funcional</h3>
                  <EditableText value={dget(doc.physical_exam, 'functional_assessment')} fieldPath="physical_exam.functional_assessment" {...editProps} emptyPlaceholder="Avaliação funcional" />
                </div>
              </div>
            </div>

            {/* Diagnóstico Funcional */}
            <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-xl border p-6`}>
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-4`}>Diagnóstico Funcional</h2>
              <div className="space-y-4">
                <div data-field="functional_diagnosis.cif_diagnosis" className={highlightClass('functional_diagnosis.cif_diagnosis')}>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2 flex items-center gap-2`}>
                    {missingFieldSet.has('functional_diagnosis.cif_diagnosis') && (
                      <AlertCircle className="h-4 w-4 text-orange-500" data-testid="missing-field-icon" />
                    )}
                    Diagnóstico CIF
                  </h3>
                  <EditableText value={dget(doc.functional_diagnosis, 'cif_diagnosis')} fieldPath="functional_diagnosis.cif_diagnosis" {...editProps} emptyPlaceholder="Diagnóstico funcional CIF" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Problemas Identificados</h3>
                  <EditableText value={dget(doc.functional_diagnosis, 'identified_problems')} fieldPath="functional_diagnosis.identified_problems" multiline {...editProps} emptyPlaceholder="Nenhum problema identificado" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Fatores Contextuais</h3>
                  <EditableText value={dget(doc.functional_diagnosis, 'contextual_factors')} fieldPath="functional_diagnosis.contextual_factors" multiline {...editProps} emptyPlaceholder="Nenhum fator contextual" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Prognóstico</h3>
                  <EditableText value={dget(doc.functional_diagnosis, 'prognosis')} fieldPath="functional_diagnosis.prognosis" {...editProps} emptyPlaceholder="Prognóstico" />
                </div>
              </div>
            </div>

            {/* Plano de Tratamento */}
            <div
              data-field="treatment_plan"
              className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-xl border p-6 ${
                (missingFieldSet.has('treatment_plan') || missingFieldSet.has('treatment_plan.phase.objectives') || missingFieldSet.has('treatment_plan.phase.techniques'))
                  ? 'border-l-4 border-l-orange-500'
                  : ''
              }`}
            >
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-4 flex items-center gap-2`}>
                {(missingFieldSet.has('treatment_plan') || missingFieldSet.has('treatment_plan.phase.objectives') || missingFieldSet.has('treatment_plan.phase.techniques')) && (
                  <AlertCircle className="h-4 w-4 text-orange-500" data-testid="missing-field-icon" />
                )}
                Plano de Tratamento
              </h2>
              <div className="space-y-6">
                {toArr(doc.treatment_plan).length ? toArr(doc.treatment_plan).map((phase, idx) => (
                  <div key={idx} className={`${isDark ? 'bg-slate-900/50' : 'bg-slate-50'} rounded-lg p-4`}>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-3`}>
                      Fase {dget(phase, 'phase_number') ?? (idx + 1)}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-700'} mb-1`}>Duração:</p>
                        <EditableText value={phase?.duration} fieldPath={`treatment_plan.${idx}.duration`} {...editProps} emptyPlaceholder="Ex: 4 semanas" />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-700'} mb-1`}>Objetivos:</p>
                        <EditableText value={phase?.objectives} fieldPath={`treatment_plan.${idx}.objectives`} multiline {...editProps} emptyPlaceholder="Objetivos terapêuticos desta fase" />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-700'} mb-1`}>Técnicas:</p>
                        <EditableText value={dget(phase, 'techniques')} fieldPath={`treatment_plan.${idx}.techniques`} multiline {...editProps} emptyPlaceholder="Técnicas e condutas a serem aplicadas" />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-700'} mb-1`}>Frequência:</p>
                        <EditableText value={phase?.frequency} fieldPath={`treatment_plan.${idx}.frequency`} {...editProps} emptyPlaceholder="Ex: 2x por semana" />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-700'} mb-1`}>Critérios de Progressão:</p>
                        <EditableText value={phase?.progression_criteria} fieldPath={`treatment_plan.${idx}.progression_criteria`} multiline {...editProps} emptyPlaceholder="Critérios para progressão de fase" />
                      </div>
                    </div>
                  </div>
                )) : (
                  editable ? null : (
                    <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Nenhum plano de tratamento definido</p>
                  )
                )}

                {/* Add-phase affordance — only when editable (in_review). */}
                {editable && (
                  <Button
                    variant="outline"
                    onClick={handleAddPhase}
                    disabled={addingPhase}
                    className={`w-full border-dashed ${isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-300 hover:bg-slate-100'}`}
                    data-testid="add-phase-btn"
                  >
                    {addingPhase ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adicionando...</>
                    ) : toArr(doc.treatment_plan).length === 0 ? (
                      <>+ Adicionar Fase</>
                    ) : (
                      <>+ Adicionar outra fase</>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Orientações ao Paciente */}
            <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-xl border p-6`}>
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-4`}>Orientações ao Paciente</h2>
              <div className="space-y-4">
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Exercícios Domiciliares</h3>
                  <EditableText value={dget(doc.patient_guidelines, 'home_exercises')} fieldPath="patient_guidelines.home_exercises" multiline {...editProps} emptyPlaceholder="Exercícios e orientações para casa" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Precauções</h3>
                  <EditableText value={doc.patient_guidelines?.precautions} fieldPath="patient_guidelines.precautions" multiline {...editProps} emptyPlaceholder="Precauções e cuidados" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Recomendações</h3>
                  <EditableText value={doc.patient_guidelines?.recommendations} fieldPath="patient_guidelines.recommendations" multiline {...editProps} emptyPlaceholder="Recomendações gerais" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Sinais de Alerta</h3>
                  <EditableText value={doc.patient_guidelines?.warning_signs} fieldPath="patient_guidelines.warning_signs" multiline {...editProps} emptyPlaceholder="Sinais que indicam alerta" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>Metas Funcionais</h3>
                  <EditableText value={doc.patient_guidelines?.functional_goals} fieldPath="patient_guidelines.functional_goals" multiline {...editProps} emptyPlaceholder="Metas funcionais do paciente" />
                </div>
              </div>
            </div>
          </div>
            );
          })()
        ) : (
          <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-xl border p-12 text-center`}>
            <FileText className={`h-16 w-16 ${isDark ? 'text-slate-600' : 'text-slate-300'} mx-auto mb-4`} />
            <p className={`mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Documentação ainda não gerada</p>
            <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Esta consulta ainda não possui documentação clínica gerada pela IA.</p>
          </div>
        )}

        {/* SRC-01 — Síntese de Raciocínio Clínico (only for avaliacao consultations) */}
        {consultation && consultation.consultation_type === 'avaliacao' && doc && (
          <SRCPanel
            consultationId={id}
            transcription={consultation.transcription}
            consultationType={consultation.consultation_type}
            templateType={consultation.template_type}
            existingSrcData={consultation.src_data || null}
            srcConfirmedInitial={Boolean(consultation.src_confirmed)}
            onConfirmed={() => { setSrcReady(true); loadConsultation(); }}
            onBypassed={() => setSrcReady(true)}
          />
        )}

        {/* ATTACH-01 — Clinical attachments (hidden while DRAFT) */}
        {consultation && consultation.status !== 'draft' && (
          <div className="mt-6">
            <AttachmentsPanel
              consultation={consultation}
              onChange={(updated) =>
                setConsultation((prev) => (prev ? { ...prev, attachments: updated } : prev))
              }
            />
          </div>
        )}

        {/* P1.3 — Histórico de alterações (audit trail) */}
        {consultation && <AuditTrailSection consultationId={id} />}
      </div>

      {/* COFFITO 414 — Missing required fields modal (shown after server 422). */}
      {showMissingModal && missingFields && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-testid="incomplete-doc-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className={`${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'} max-w-lg w-full rounded-2xl border shadow-2xl p-6`}>
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-full bg-orange-100 dark:bg-orange-900/30 p-2">
                <AlertCircle className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Documento Incompleto</h3>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  Os seguintes campos obrigatórios precisam ser preenchidos antes da aprovação:
                </p>
              </div>
            </div>

            <div className={`max-h-72 overflow-y-auto mb-5 rounded-lg border ${isDark ? 'border-slate-800 bg-slate-950/30' : 'border-slate-200 bg-slate-50'} p-3`}>
              {(() => {
                const grouped = {};
                missingFields.forEach((m) => {
                  if (!grouped[m.section]) grouped[m.section] = [];
                  grouped[m.section].push(m.label);
                });
                return Object.entries(grouped).map(([section, labels]) => (
                  <div key={section} className="mb-3 last:mb-0">
                    <h4 className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-slate-500'} mb-1`}>
                      {section}
                    </h4>
                    <ul className={`list-disc list-inside text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'} space-y-1`}>
                      {labels.map((label, i) => (
                        <li key={i}>{label}</li>
                      ))}
                    </ul>
                  </div>
                ));
              })()}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => { setShowMissingModal(false); }}
                data-testid="incomplete-doc-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  // Close modal but KEEP missingFields populated so highlights persist.
                  setShowMissingModal(false);
                  scrollToFirstMissingField();
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="incomplete-doc-edit-btn"
              >
                Editar Documento
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* P2 AUTOSAVE — conflict resolution dialog (blocking). */}
      {autosave.conflict && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-testid="autosave-conflict-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className={`${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'} max-w-lg w-full rounded-2xl border shadow-2xl p-6`}>
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-full bg-orange-100 dark:bg-orange-900/30 p-2">
                <AlertCircle className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Conflito de edição
                </h3>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  Este documento foi editado em outra aba ou dispositivo. Suas alterações locais ainda
                  não foram sincronizadas. Escolha como resolver:
                </p>
              </div>
            </div>

            <div className={`text-xs mb-5 rounded-lg border p-3 ${isDark ? 'border-slate-800 bg-slate-950/30 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
              <p className="mb-2"><strong>Manter minhas alterações:</strong> descarta a versão do servidor e envia suas edições locais.</p>
              <p><strong>Usar versão do servidor:</strong> descarta suas alterações locais e recarrega a versão mais recente do servidor.</p>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => autosave.resolveConflict('loadServer')}
                className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}
                data-testid="autosave-conflict-load-server"
              >
                Usar versão do servidor
              </Button>
              <Button
                onClick={() => autosave.resolveConflict('keepMine')}
                className="bg-primary hover:bg-primary-hover text-white"
                data-testid="autosave-conflict-keep-mine"
              >
                Manter minhas alterações
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* P4 — Archive / Restore / Delete confirmation dialog */}
      {p4Dialog.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="p4-confirm-modal"
        >
          <div className={`${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'} max-w-md w-full rounded-2xl border shadow-2xl p-6`}>
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {p4Dialog.mode === 'archive' && 'Arquivar documento'}
              {p4Dialog.mode === 'restore' && 'Restaurar documento'}
              {p4Dialog.mode === 'delete' && 'Excluir documento'}
            </h3>
            <p className={`text-sm mb-5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {p4Dialog.mode === 'archive' && (
                <>O documento sai da lista principal, mas continua acessível no arquivo. <strong>O crédito consumido não é estornado.</strong> Você poderá restaurar a qualquer momento.</>
              )}
              {p4Dialog.mode === 'restore' && (
                <>O documento volta para o estado aprovado e reaparece nas listas.</>
              )}
              {p4Dialog.mode === 'delete' && (
                <>O documento entra em soft-delete. Se estiver dentro da janela de 3 dias após a aprovação e você ainda tiver estornos automáticos disponíveis no ciclo, o crédito será devolvido automaticamente. Caso contrário, a exclusão ainda ocorre e o pedido de estorno vai para revisão manual.</>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setP4Dialog({ open: false, mode: null })}
                disabled={p4Working}
                data-testid="p4-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleP4Confirm}
                disabled={p4Working}
                className={p4Dialog.mode === 'delete'
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-primary hover:bg-primary-hover text-white'}
                data-testid="p4-confirm-btn"
              >
                {p4Working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {p4Dialog.mode === 'archive' && 'Arquivar'}
                {p4Dialog.mode === 'restore' && 'Restaurar'}
                {p4Dialog.mode === 'delete' && 'Excluir'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
